// THE SHELL KEYBOARD REGISTRY — one registry, one keydown handler, one guard,
// one off switch (HOME_AMBIENT_SKIN_SPEC §5 · DESIGN-WL-SHELL-KEYBOARD-MOTION-1).
//
//   el.call('registerShortcut', { id, keys, seq, label, group, when, run })
//                                            → unregister(): boolean
//   el.call('listShortcuts')                 → rows for the ? sheet and the palette
//   el.call('shortcutSheetGroups')           → the sheet's rows, grouped (live keys only)
//   el.call('handleShortcutKeydown', el, s, event)
//                                            → true when a binding took the key
//   el.call('runShortcut', id)               → run one row's command (the palette)
//   el.call('shortcutBlockReason', el, s, event, kind)
//                                            → why the guard refuses, or null
//
// A BINDING. `keys` is a chord string or an array of alternatives — 'mod+k',
// '/', '?', 'j', 'escape', ['1', …, '9']; `mod` is ⌘ or Ctrl (either one, on
// every platform — the HeaderSearch rule). `seq` is a sequence, ['g', 'h'],
// whose keys must follow each other inside 1.5 s. `when(el, s)` gates it on
// live state; `run(el, s, event)` performs it and may return `false` to
// decline (the key then stays the browser's, and an older binding for the same
// key may take it). `label` / `group` are bundle KEYS — the sheet and the
// palette resolve them through polyglot. `adopted` names the component that
// already owns the key: the row is LISTED (sheet, palette) and never run here,
// so a key is never bound twice. `repeat: true` lets a held key walk.
// Re-registering an id replaces the binding; the most recent registration of a
// key wins, so a surface's own binding layers over the shell's.
//
// THE KINDS the guard reads: `chord` (carries ⌘/Ctrl/Alt), `char` (one
// printable character), `seq` (a sequence), `named` (Escape, Enter).
//
// THE GUARD (spec §5 "Guard") — `shortcutBlockReason`:
//   • a keystroke already handled by its surface (`defaultPrevented`) is never
//     handled again — mail's page keys, a card's Enter, a dialog's Escape win;
//   • an IME composition (`isComposing`, keyCode 229 — Georgian/CJK) runs
//     nothing;
//   • ⌘ / Ctrl / Alt held: only chords run (never shadows ⌘1–9, ⌘L, Ctrl+J);
//   • focus in a field — input, textarea, select, [contenteditable], role
//     textbox / combobox / searchbox — only chords run;
//   • an OPEN OVERLAY owns the keyboard, and nothing runs. The shell exposes
//     "an overlay is open" through these carriers (read, never written here):
//       – focus inside [role=dialog] / [role=alertdialog] / [aria-modal=true] /
//         [role=menu] / [role=listbox] — AppDialog v2's Panel, the legacy
//         Modal Window, the windows ModalWindow, house menus and listboxes
//         (every one moves focus in when it opens);
//       – a DISPLAYED non-kit modal with focus OUTSIDE it — [aria-modal=true]
//         (the marketplace detail; no [data-open] root required — the slot
//         signal is html[data-slot]): its own handler closes it, the shell
//         waits;
//       – the windows ModalRoot stack  — shellRoot.ModalRoot.state.stack;
//       – the DropdownRoot layer       — shellRoot.DropdownRoot.state.layer;
//       – the house context menu       — root.contextDropdown;
//       – the widget colour palette    — root._homeColorPicker;
//       – this registry's own ? sheet   — root._shortcutsSheetOpen;
//       – the header search            — root._headerSearchOpen;
//       – the header drawers           — root._chatDrawerOpen,
//         root._mailDrawerOpen, root.notificationsPanelOpen
//         (shared/components/headerOverlayState.js; the assistant is DOCKED
//         beside the page, not over it, so it blocks nothing).
//     A PageSlot app window is NOT an overlay here: it is a surface, and the
//     board bindings that it covers check `_canvasSlot` themselves.
//
// TWO SWITCHES:
//   • the WORKSPACE switch — the `shell.keyboardShortcuts` feature flag
//     (shared/functions/featureFlagsData.js FEATURE_SUBFLAGS.shell). Off: the
//     registry stands down;
//   • the PERSON's switch (WCAG 2.1.4 Character Key Shortcuts) — the user
//     preference `shortcutsSingleKeys === false` on `root.userPrefs` (the flat
//     user_preferences row `themeMode` lives on; the Settings › Accessibility
//     row that writes it is ws-w2's). Off: `char` and `seq` stop; chords stay.
//   Escape and Enter are not character keys — dismissal and activation — so
//   neither switch silences a `named` binding. Both switches silence
//   KEYSTROKES only: `runShortcut` (a palette pick) is a command, not a key.
//
// STORAGE. The registry lives on the shell ROOT element (`__shortcuts`), never
// in module scope: every export here is serialized ALONE for platform delivery
// (frank), so none of them may lean on a module-scope binding or helper — each
// carries its own small readers (the house rule, shared/functions/themeMode.js).
//
// DEFAULTS are armed lazily, on the first key or listing (`registerDefaultShortcuts`,
// functions/shortcutsDefault.js), so the shell's one listener (the shell
// root's declared `onDocumentKeydown`, app.js) stays ONE call.

export function registerShortcut(def) {
  const origin = this
  const host = (origin && typeof origin.getRoot === 'function' && origin.getRoot()) || origin
  if (!host || !def || !def.id) return () => false
  const store =
    host.__shortcuts || (host.__shortcuts = { list: [], pending: null, armed: false, last: null })
  const NAMED = { esc: 'escape', escape: 'escape', enter: 'enter', return: 'enter', space: ' ' }
  const parse = (chord) => {
    const parts = String(chord).toLowerCase().split('+')
    const key = parts.pop()
    const mods = new Set(parts)
    return {
      key: NAMED[key] || key,
      mod: mods.has('mod') || mods.has('cmd') || mods.has('meta') || mods.has('ctrl'),
      alt: mods.has('alt') || mods.has('option'),
      shift: mods.has('shift')
    }
  }
  const keys = def.keys == null ? [] : Array.isArray(def.keys) ? def.keys : [def.keys]
  const alts = keys.map(parse)
  const seq = Array.isArray(def.seq) && def.seq.length ? def.seq.map((k) => parse(k).key) : null
  const kind = seq
    ? 'seq'
    : alts.some((a) => a.mod || a.alt)
      ? 'chord'
      : alts.length && alts.every((a) => a.key.length === 1)
        ? 'char'
        : 'named'
  const rec = {
    id: String(def.id),
    label: def.label || null,
    group: def.group || null,
    groupOrder: typeof def.groupOrder === 'number' ? def.groupOrder : 0,
    order: typeof def.order === 'number' ? def.order : store.list.length,
    alts,
    seq,
    kind,
    when: typeof def.when === 'function' ? def.when : null,
    run: typeof def.run === 'function' ? def.run : null,
    repeat: !!def.repeat,
    adopted: def.adopted || null,
    keycaps: Array.isArray(def.keycaps) ? def.keycaps.map(String) : null,
    joiner: def.joiner || (seq ? 'then' : null)
  }
  const at = store.list.findIndex((r) => r.id === rec.id)
  if (at >= 0) store.list.splice(at, 1)
  store.list.push(rec)
  return () => {
    const i = store.list.indexOf(rec)
    if (i < 0) return false
    store.list.splice(i, 1)
    return true
  }
}

// The shell's overlay layers are read by THE ONE reader in the shared kit
// (@symbo-ls/workspace-shared functions/shellOverlayOpen.js — moved there from
// this file so the standalone marketplace reads the same one), reached through
// the element's registry: `el.call('shellOverlayOpen')`.

// Why the guard refuses a keystroke of `kind` right now — 'flag', 'offSwitch',
// 'composing', 'modifier', 'field', 'overlay:<carrier>' — or null. With no
// event only the two switches are read (a listing, a palette pick).
export function shortcutBlockReason(el, s, event, kind) {
  const origin = el || this
  const host = (origin && typeof origin.getRoot === 'function' && origin.getRoot()) || origin
  const st = s || (host && host.state)
  const root = (st && st.root) || st || {}
  const e = event || null
  const t = e && e.target
  // The keystroke itself first — the cheap reads, and the ones every keypress
  // typed into a field reaches.
  if (e) {
    if (e.isComposing || e.keyCode === 229) return 'composing'
    if (kind !== 'chord' && (e.metaKey || e.ctrlKey || e.altKey)) return 'modifier'
    if (kind !== 'chord' && t && t.nodeType === 1) {
      const tag = String(t.tagName || '').toLowerCase()
      const attr = (name) => (typeof t.getAttribute === 'function' ? t.getAttribute(name) : null)
      const editable = attr('contenteditable')
      const role = attr('role')
      if (
        t.isContentEditable ||
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        (editable !== null && editable !== 'false') ||
        role === 'textbox' ||
        role === 'combobox' ||
        role === 'searchbox'
      ) {
        return 'field'
      }
    }
  }
  // The two switches.
  if (kind !== 'named') {
    let on = true
    try {
      on =
        !host ||
        typeof host.call !== 'function' ||
        host.call('featureEnabled', root, 'shell.keyboardShortcuts') !== false
    } catch (_) {
      on = true
    }
    if (!on) return 'flag'
  }
  const prefs = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
  if ((kind === 'char' || kind === 'seq') && prefs && prefs.shortcutsSingleKeys === false) {
    return 'offSwitch'
  }
  if (!e) return null
  // The overlay carriers — see the file header for what each one is.
  if (
    t &&
    typeof t.closest === 'function' &&
    t.closest(
      '[role="dialog"],[role="alertdialog"],[aria-modal="true"],[role="menu"],[role="listbox"]'
    )
  ) {
    return 'overlay:focus'
  }
  // The shell's ONE overlay reader (shared functions/shellOverlayOpen.js),
  // through the calling element's registry — the guard is always called as
  // `host.call('shortcutBlockReason', host, …)`, so `origin` is that element.
  // The assistant's MAIN LAYER over a page ('mainThread', AI_ASSISTANT_SPEC
  // §12.2) is a layer over the PAGE, not over the shell: this registry is the
  // shell's own and sits above it (`/` still reaches the composer the thread
  // answers through, the layer's own Esc row closes it), so it blocks nothing
  // here — only the page's lower keyboard layers stand down under it.
  const layer =
    origin && typeof origin.call === 'function' ? origin.call('shellOverlayOpen', host) : null
  if (layer && layer !== 'mainThread') return 'overlay:' + layer
  if (root.contextDropdown && root.contextDropdown.open !== false) return 'overlay:contextMenu'
  if (root._homeColorPicker) return 'overlay:colorPicker'
  if (root._shortcutsSheetOpen) return 'overlay:sheet'
  if (root._headerSearchOpen) return 'overlay:search'
  if (root._chatDrawerOpen || root._mailDrawerOpen || root.notificationsPanelOpen) {
    return 'overlay:drawer'
  }
  // LAST, a modal every carrier above missed — the marketplace detail, any
  // DISPLAYED [aria-modal="true"]. Focus INSIDE it already answered
  // 'overlay:focus'; this carrier covers focus OUTSIDE it — a closed
  // overlay above the modal returns focus to its header trigger, and the
  // shell's escape binding then closed the modal's whole window on one
  // press (live, dev 92ddd7899, 2026-10-21). The modal's own document
  // handler closes it and spends the key; the shell stands down. Candidates
  // come from the element tree (lookdownAll, FA501); openness from the
  // node's own markup, because the kit keeps closed dialogs mounted — a
  // mounted-but-closed dialog must never lock the shell's keyboard:
  //   • a closed marker above — [data-open="false"], [aria-hidden="true"],
  //     `hidden` (the windows kit marks closed roots so);
  //   • a kit dialog root above — [data-presentation] — that misses
  //     [data-open]: an AppDialog root gains [data-open] only while open,
  //     and a cascade-less test DOM cannot read its closed display;
  //   • computed display/visibility, for everything else.
  // NO open [data-open] root is REQUIRED: the live marketplace detail has
  // none (the slot's signal is html[data-slot], measured on dev 92ddd7899).
  const modalHosts =
    host && typeof host.lookdownAll === 'function'
      ? host.lookdownAll(
          (child) =>
            child &&
            child.node &&
            typeof child.node.getAttribute === 'function' &&
            child.node.getAttribute('aria-modal') === 'true'
        )
      : null
  if (modalHosts && modalHosts.length) {
    const view =
      typeof window !== 'undefined' && typeof window.getComputedStyle === 'function' ? window : null
    // Geometry runs only where layout exists (the shell root's own box):
    // live, a closed kit dialog still measures 0×0 behind opacity/width
    // tricks a style read misses (five such at rest on dev 92ddd7899); a
    // test DOM (jsdom, the recording DOM) has no layout and every rect is
    // 0×0, so there the markup walks above are the whole judgment.
    let laidOut = false
    const rootNode = host && host.node
    if (rootNode && typeof rootNode.getBoundingClientRect === 'function') {
      try {
        const rr = rootNode.getBoundingClientRect()
        laidOut = !!rr && rr.width > 0 && rr.height > 0
      } catch (_) {}
    }
    for (const m of modalHosts) {
      const node = m.node
      if (t && typeof node.contains === 'function' && node.contains(t)) continue
      let closed = false
      let n = node
      while (n && typeof n.getAttribute === 'function') {
        if (
          n.getAttribute('data-open') === 'false' ||
          n.getAttribute('aria-hidden') === 'true' ||
          n.hidden
        ) {
          closed = true
          break
        }
        if (n.getAttribute('data-presentation') != null) {
          if (n.getAttribute('data-open') == null) closed = true
          break
        }
        n = n.parentElement
      }
      if (closed) continue
      if (view) {
        try {
          const cs = view.getComputedStyle(node)
          if (cs && (cs.display === 'none' || cs.visibility === 'hidden')) continue
        } catch (_) {}
      }
      if (laidOut && typeof node.getBoundingClientRect === 'function') {
        const r = node.getBoundingClientRect()
        if (!r || r.width <= 0 || r.height <= 0) continue
      }
      return 'overlay:modal'
    }
  }
  return null
}

// THE ONE HANDLER. Wired once, from the shell root's declared
// `onDocumentKeydown` (app.js — DOMQL owns the listener, FA503), as a
// BUBBLE-phase document keydown listener — a surface's own handler runs first
// and claims a key with preventDefault, which is honoured below.
export function handleShortcutKeydown(el, s, event) {
  const origin = el || this
  const host = (origin && typeof origin.getRoot === 'function' && origin.getRoot()) || origin
  const e = event
  if (!host || !e) return false
  const store =
    host.__shortcuts || (host.__shortcuts = { list: [], pending: null, armed: false, last: null })
  // One keystroke, one pass: a second listener handed the SAME event never
  // re-runs a binding.
  if (store.last === e) return false
  store.last = e
  if (e.defaultPrevented) {
    store.pending = null
    return false
  }
  if (e.isComposing || e.keyCode === 229) return false
  if (!store.armed) {
    store.armed = true
    try {
      host.call('registerDefaultShortcuts')
    } catch (_) {
      /* a shell without the defaults keeps an empty registry */
    }
  }
  const st = s || host.state
  const root = (st && st.root) || st || {}
  // Agent mode (WS-AGENT-MODE-1) owns the whole keyboard: its frame forwards
  // every key to the shared runtime, and no shell binding runs under it.
  if (root.agentMode === true || root._agentModeLink === true) {
    store.pending = null
    return false
  }

  // The key, layout-aware: a Latin letter as itself; the physical key's Latin
  // letter on a non-Latin layout (Georgian ჯ on the J key is J); any other
  // character as typed ('?', '/', digits); a named key lower-cased.
  const raw = typeof e.key === 'string' ? e.key : ''
  let k = ''
  if (raw.length === 1) {
    const lower = raw.toLowerCase()
    if (lower >= 'a' && lower <= 'z') k = lower
    else if (raw.charCodeAt(0) > 127 && /^Key[A-Z]$/.test(e.code || '')) {
      k = e.code.slice(3).toLowerCase()
    } else k = raw
  } else k = raw.toLowerCase() === 'esc' ? 'escape' : raw.toLowerCase()
  // A lone modifier (Shift before '?'), a dead key, an IME process key: not a
  // keystroke of its own — an open sequence stays open across it.
  if (
    !k ||
    k === 'shift' ||
    k === 'control' ||
    k === 'alt' ||
    k === 'meta' ||
    k === 'dead' ||
    k === 'process' ||
    k === 'unidentified'
  ) {
    return false
  }

  const list = store.list
  const now = typeof e.timeStamp === 'number' && e.timeStamp > 0 ? e.timeStamp : Date.now()
  const blocked = (kind) => !!host.call('shortcutBlockReason', host, root, e, kind)
  // PLAIN TYPING IS NEVER A SHORTCUT (round 1b, sam — keyboard only: with
  // focus on the page body, a typed sentence opened Calendar; the "4" of
  // "Tuesday 4 to 6 pm" pressed the fourth app tile). A character key that
  // comes within a second of a character that NAMES NO BINDING, typed where a
  // single key could have run (not in a field, no overlay), is typing: no
  // single-key or sequence binding runs for it, and it cannot open a sequence.
  // Chords and named keys are not typing; a key that names a binding — run,
  // declined or held off — is a shortcut attempt, never typing (J J J walks the
  // widgets; P pressed off a card and then on one pins it); the second key of
  // an open sequence belongs to the sequence. The first key after a pause
  // stays a shortcut — one keystroke cannot tell a person typing from a person
  // pressing a key — and the person's own switch (shortcutsSingleKeys) turns
  // single keys off altogether.
  const printable = raw.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey
  const typing = printable && store.typedAt != null && now - store.typedAt < 1000
  const seqAttempt = !!store.pending
  let named = false
  const typed = () => {
    if (printable && !seqAttempt && !named && !blocked('char')) store.typedAt = now
    return false
  }
  const matches = (a) => {
    if (a.key !== k) return false
    const modHeld = !!(e.metaKey || e.ctrlKey)
    if (a.mod !== modHeld) return false
    if (a.mod && !!a.alt !== !!e.altKey) return false
    if (a.shift && !e.shiftKey) return false
    // Shift+J is not J — but '?' and '/' carry Shift on most layouts
    if (!a.shift && e.shiftKey && a.key.length === 1 && a.key >= 'a' && a.key <= 'z') return false
    return true
  }

  // 1. An open sequence: does this key complete (or extend) one?
  if (store.pending) {
    const p = store.pending
    store.pending = null
    if (!e.repeat && now - p.at <= 1500 && !blocked('seq')) {
      const typed = p.keys.concat(k)
      for (let i = list.length - 1; i >= 0; i--) {
        const b = list[i]
        if (!b.seq || !b.run || b.seq.length !== typed.length) continue
        if (!b.seq.every((x, j) => x === typed[j])) continue
        if (b.when && !b.when(host, root)) continue
        if (b.run(host, root, e) === false) continue
        e.preventDefault()
        return true
      }
      const longer = list.some(
        (b) =>
          b.seq && b.run && b.seq.length > typed.length && typed.every((x, j) => b.seq[j] === x)
      )
      if (longer) {
        store.pending = { keys: typed, at: now }
        return false
      }
    }
    // no sequence took it — the key is read on its own below
  }

  // 2. Chords, characters, named keys — the most recent registration first.
  for (let i = list.length - 1; i >= 0; i--) {
    const b = list[i]
    if (!b.run || b.seq || !b.alts.some(matches)) continue
    named = true
    if (typing && b.kind === 'char') continue
    if (blocked(b.kind)) continue
    if (e.repeat && !b.repeat) return false
    if (b.when && !b.when(host, root)) continue
    if (b.run(host, root, e) === false) continue
    e.preventDefault()
    return true
  }

  // 3. The first key of a sequence opens it (and is not consumed) — never
  // mid-typing.
  const opensSeq = list.some((b) => b.seq && b.run && b.seq[0] === k)
  if (opensSeq) named = true
  if (!typing && !e.repeat && opensSeq && !blocked('seq')) {
    store.pending = { keys: [k], at: now }
    return false
  }
  // Nothing took the key: a character typed — the next one within a second
  // is typing too.
  return typed()
}

// Every labelled row: { id, label, group, groupOrder, order, keycaps, joiner,
// sequence, adopted, runnable, active }. `active` answers "does the KEYSTROKE
// work now" (the two switches) — the sheet renders only active rows.
// `runnable` answers "can the COMMAND run here, now": a `run` AND the row's
// own `when` for the live root (a `when` that throws answers no) — the ⌘K
// palette offers only runnable rows (functions/search/searchCommandResults.js)
// and shows keycaps only where active. Before, `runnable` was just "has a
// run": the palette listed "Customize dashboard" off the home board, and a
// pick ran nothing.
export function listShortcuts() {
  const origin = this
  const host = (origin && typeof origin.getRoot === 'function' && origin.getRoot()) || origin
  if (!host) return []
  const store =
    host.__shortcuts || (host.__shortcuts = { list: [], pending: null, armed: false, last: null })
  if (!store.armed) {
    store.armed = true
    try {
      host.call('registerDefaultShortcuts')
    } catch (_) {
      /* a shell without the defaults keeps an empty registry */
    }
  }
  const st = host.state
  const root = (st && st.root) || st || {}
  const nav = typeof navigator === 'undefined' ? null : navigator
  const platform = String(
    (nav && ((nav.userAgentData && nav.userAgentData.platform) || nav.platform || nav.userAgent)) ||
      ''
  )
  const mac = /mac|iphone|ipad|ipod/i.test(platform)
  // Key legends as printed on the key (Latin on every layout, never translated).
  const capOf = (key) =>
    key === 'escape'
      ? 'Esc'
      : key === 'enter'
        ? '↵'
        : key === ' '
          ? 'Space'
          : key.length === 1
            ? key.toUpperCase()
            : key.charAt(0).toUpperCase() + key.slice(1)
  const rows = []
  for (const b of store.list) {
    if (!b.label) continue
    let ready = !!b.run
    if (ready && b.when) {
      try {
        ready = !!b.when(host, root)
      } catch (_) {
        ready = false
      }
    }
    let keycaps = b.keycaps
    if (!keycaps && b.seq) keycaps = b.seq.map(capOf)
    if (!keycaps && b.alts.length) {
      const a = b.alts[0]
      keycaps = []
      if (a.mod) keycaps.push(mac ? '⌘' : 'Ctrl')
      if (a.alt) keycaps.push(mac ? '⌥' : 'Alt')
      if (a.shift) keycaps.push('⇧')
      keycaps.push(capOf(a.key))
    }
    rows.push({
      id: b.id,
      label: b.label,
      group: b.group,
      groupOrder: b.groupOrder,
      order: b.order,
      keycaps: keycaps || [],
      joiner: b.joiner,
      sequence: !!b.seq,
      adopted: !!b.adopted,
      runnable: ready,
      active: !host.call('shortcutBlockReason', host, root, null, b.kind)
    })
  }
  return rows.sort((a, b) => a.groupOrder - b.groupOrder || a.order - b.order)
}

// The sheet's groups: consecutive live rows sharing a group label.
export function shortcutSheetGroups() {
  const rows = (this.call('listShortcuts') || []).filter((r) => r.active)
  const groups = []
  for (const r of rows) {
    let g = groups[groups.length - 1]
    if (!g || g.label !== r.group) {
      g = { key: 'group' + groups.length, label: r.group, rows: [] }
      groups.push(g)
    }
    g.rows.push({ key: r.id, ...r })
  }
  return groups
}

// A palette pick: runs the row's COMMAND. The keystroke switches do not apply
// (a pick is not a character-key shortcut); `when` does.
export function runShortcut(id) {
  const origin = this
  const host = (origin && typeof origin.getRoot === 'function' && origin.getRoot()) || origin
  if (!host) return false
  const store =
    host.__shortcuts || (host.__shortcuts = { list: [], pending: null, armed: false, last: null })
  if (!store.armed) {
    store.armed = true
    try {
      host.call('registerDefaultShortcuts')
    } catch (_) {
      /* a shell without the defaults keeps an empty registry */
    }
  }
  const b = store.list.find((r) => r.id === id)
  if (!b || !b.run) return false
  const st = host.state
  const root = (st && st.root) || st || {}
  if (b.when && !b.when(host, root)) return false
  return b.run(host, root, null) !== false
}
